import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, Loader2, Calendar, ArrowRight } from "lucide-react";
import { API } from "@/App";
import axios from "axios";

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading");
  const [paymentData, setPaymentData] = useState(null);
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    }
  }, [sessionId]);

  const pollPaymentStatus = async (attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus("timeout");
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/checkout/status/${sessionId}`);
      setPaymentData(response.data);

      if (response.data.payment_status === "paid") {
        setStatus("success");
        return;
      } else if (response.data.status === "expired") {
        setStatus("expired");
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    } catch (error) {
      console.error("Error checking payment status:", error);
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center p-6">
      <div className="max-w-md w-full">
        {status === "loading" && (
          <div className="text-center animate-fade-in" data-testid="payment-loading">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-10 h-10 text-emerald-600 animate-spin" />
            </div>
            <h1 className="font-display text-2xl font-bold text-emerald-950 mb-2">
              Processing Payment
            </h1>
            <p className="text-stone-600">
              Please wait while we confirm your payment...
            </p>
          </div>
        )}

        {status === "success" && (
          <div className="text-center animate-fade-in" data-testid="payment-success">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-emerald-600" />
            </div>
            <h1 className="font-display text-2xl font-bold text-emerald-950 mb-2">
              Payment Successful!
            </h1>
            <p className="text-stone-600 mb-6">
              Your appointment has been confirmed. We've sent a confirmation to your email.
            </p>
            
            {paymentData && (
              <div className="bg-white border border-stone-200 rounded-lg p-6 mb-6 text-left">
                <h3 className="font-medium text-emerald-950 mb-4">Payment Details</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-stone-600">Amount Paid</span>
                    <span className="font-medium text-emerald-950">
                      ${(paymentData.amount_total / 100).toFixed(2)} {paymentData.currency?.toUpperCase()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-stone-600">Status</span>
                    <span className="font-medium text-emerald-600">Confirmed</span>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-col gap-3">
              <Button 
                className="btn-primary rounded-sm w-full"
                onClick={() => navigate("/dashboard")}
                data-testid="go-to-dashboard-btn"
              >
                <Calendar className="w-5 h-5 mr-2" />
                View My Appointments
              </Button>
              <Button 
                variant="outline"
                className="w-full"
                onClick={() => navigate("/book")}
              >
                Book Another Appointment
              </Button>
            </div>
          </div>
        )}

        {status === "expired" && (
          <div className="text-center animate-fade-in" data-testid="payment-expired">
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-amber-600" />
            </div>
            <h1 className="font-display text-2xl font-bold text-emerald-950 mb-2">
              Session Expired
            </h1>
            <p className="text-stone-600 mb-6">
              Your payment session has expired. Please try booking again.
            </p>
            <Button 
              className="btn-primary rounded-sm"
              onClick={() => navigate("/book")}
            >
              Book Again
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        )}

        {(status === "error" || status === "timeout") && (
          <div className="text-center animate-fade-in" data-testid="payment-error">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
            <h1 className="font-display text-2xl font-bold text-emerald-950 mb-2">
              Payment Issue
            </h1>
            <p className="text-stone-600 mb-6">
              We couldn't confirm your payment. Please check your email for confirmation or contact support.
            </p>
            <div className="flex flex-col gap-3">
              <Button 
                className="btn-primary rounded-sm w-full"
                onClick={() => navigate("/dashboard")}
              >
                Go to Dashboard
              </Button>
              <Button 
                variant="outline"
                className="w-full"
                onClick={() => window.location.reload()}
              >
                Try Again
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
